/**
 * Feuille de brouillon — une feuille par question, gardée pendant toute la
 * tentative : on peut la fermer, revenir au QCM, la rouvrir, rien n'est perdu.
 * À la reprise d'une tentative, les lignes déjà jouées sont rejouées.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ScratchSession, type SheetLine } from './scratch-session';
import {
  freeStorageKey,
  loadFreeLines,
  loadFreeTextLines,
  saveFreeLines,
  saveFreeTextLines,
} from './free-sheet';
import {
  createLineRecorder,
  erasePayload,
  linePayload,
  loadV2Ui,
  sendScratchLine,
  useScratchData,
  type ScratchMode,
  type V2Ui,
} from './scratch-data';
import type { FreeModelInput, V2Session } from '@/features/brouillon-v2';
import { withChoiceValues } from './choice-values';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function useScratchSheets({
  exerciseId,
  attemptId,
  mode,
  record,
  enabled = true,
  calculator = false,
  calculatorCostFor,
  free = null,
}: {
  exerciseId: string;
  attemptId: string | null;
  mode: ScratchMode;
  /** enregistrer les lignes en base (élève, hors aperçu / démo) */
  record: boolean;
  /** faux : l'exercice n'a aucune SKILLCARD reliée, donc aucune feuille à chercher */
  enabled?: boolean;
  /** calculatrice autorisée sur l'exercice : un calcul numérique propose son résultat */
  calculator?: boolean;
  /**
   * calculatrice NON autorisée : HORA retirés par usage pour cette question
   * (`calculatorCost`, selon sa difficulté) ; défaut 1
   */
  calculatorCostFor?: (questionId: string) => number;
  /**
   * feuilles LIBRES (1re) : une question sans feuille publiée en reçoit une pour
   * calculer, sans HORA ; `numbersFor` donne les nombres de son énoncé
   */
  free?: {
    numbersFor: (questionId: string) => string[];
    /**
     * ce que la question écrit (énoncé, propositions) : la feuille libre du moteur v2 en est construite
     * (`free-model.ts`) ; absent, la feuille libre garde l'ancienne interface
     */
    modelFor?: (questionId: string) => FreeModelInput | null;
  } | null;
}) {
  const data = useScratchData({ exerciseId, attemptId, mode, enabled });
  const sessions = useRef(new Map<string, ScratchSession>());
  /** feuilles du moteur v2 jouées avec l'interface du prototype (lot V4), reliées à `sessions` */
  const v2Sessions = useRef(new Map<string, V2Session>());
  /** modèles que le moteur n'a pas su charger : la question reste sans feuille */
  const broken = useRef(new Set<string>());
  const recorder = useMemo(() => createLineRecorder(sendScratchLine), []);
  const freeRef = useRef(free);
  freeRef.current = free;
  // feuilles libres du moteur v2 : l'interface est chargée à la demande, même sans feuille `packs`
  const [lazyUi, setLazyUi] = useState<V2Ui | null>(null);
  const uiRequested = useRef(false);
  const requestUi = useCallback(() => {
    if (uiRequested.current) return;
    uiRequested.current = true;
    loadV2Ui()
      .then((m) => setLazyUi(() => m))
      .catch((e) => console.warn('[scratch] interface v2 indisponible (feuille libre)', e));
  }, []);
  const costRef = useRef(calculatorCostFor);
  costRef.current = calculatorCostFor;

  // le réseau revient : les lignes en attente repartent aussitôt
  useEffect(() => {
    const wake = () => recorder.wake();
    window.addEventListener('online', wake);
    return () => window.removeEventListener('online', wake);
  }, [recorder]);

  // Nouvelle tentative : nouvelles feuilles. Fait pendant le rendu (idempotent)
  // pour que la carte et le panneau voient la même feuille. De nouvelles données
  // pour la MÊME tentative (questions débloquées après un achat) ajoutent des
  // feuilles sans toucher à celles en cours : elles restent la référence.
  const generation = `${exerciseId}|${mode}|${attemptId ?? ''}`;
  const genRef = useRef(generation);
  if (genRef.current !== generation) {
    sessions.current.forEach((s) => s.dispose());
    sessions.current = new Map();
    v2Sessions.current = new Map();
    broken.current = new Set();
    genRef.current = generation;
  }

  const get = useCallback(
    (questionId: string | null): ScratchSession | null => {
      if (!questionId) return null;
      const entry = data.data?.models.get(questionId);
      if (!entry) {
        // feuille libre : seulement une fois les feuilles publiées connues
        const f = freeRef.current;
        if (!f || data.isLoading) return null;
        const existing = sessions.current.get(questionId);
        if (existing) {
          existing.setCalculator(calculator);
          return existing;
        }
        const key = record ? freeStorageKey(attemptId, questionId) : null;
        const created: ScratchSession = new ScratchSession({
          free: true,
          numbers: f.numbersFor(questionId),
          calculator,
          stepDelayMs: prefersReducedMotion() ? 60 : 300,
          onCommit: key
            ? () => saveFreeLines(key, created.getSnapshot().lines.map(storedFree))
            : undefined,
          onErase: key
            ? () => saveFreeLines(key, created.getSnapshot().lines.map(storedFree))
            : undefined,
        });
        created.restoreFree(loadFreeLines(key));
        sessions.current.set(questionId, created);
        return created;
      }
      let s = sessions.current.get(questionId);
      if (!s && broken.current.has(questionId)) return null;
      if (!s) {
        try {
          s = new ScratchSession({
            // propositions « 8,4 m » sans valeur : la valeur lue du libellé (✓ sur la bonne touche)
            model: entry.track === 'packs' ? withChoiceValues(entry.model) : entry.model,
            maxHora: entry.maxHora,
            // feuille du moteur v2 (lot V3) : jugée par le module chargé avec les données
            ...(entry.track === 'packs' ? { packs: data.data?.packs, level: entry.level } : {}),
            calculator,
            calculatorCost: costRef.current?.(questionId),
            stepDelayMs: prefersReducedMotion() ? 60 : 300,
            onCommit:
              record && attemptId
                ? (line) => {
                    void recorder.record(linePayload(attemptId, questionId, line, entry.track));
                  }
                : undefined,
            onErase:
              record && attemptId
                ? (line) => {
                    void recorder.erase(erasePayload(attemptId, questionId, line));
                  }
                : undefined,
          });
          const stored = data.data?.lines.get(questionId);
          if (stored?.length) s.replay(stored);
        } catch (e) {
          // Modèle que ce moteur ne sait pas charger (feuille brouillon jamais
          // validée, moteur différent…) : la question se joue sans feuille,
          // le player n'est jamais interrompu.
          console.warn('[scratch] feuille ignorée', questionId, e);
          broken.current.add(questionId);
          return null;
        }
        sessions.current.set(questionId, s);
      }
      s.setCalculator(calculator, costRef.current?.(questionId));
      return s;
    },
    // les feuilles libres sont lues dans `freeRef` à l'appel (le player se
    // rerend quand le niveau de l'exercice arrive)
    [data.data, data.isLoading, record, attemptId, recorder, calculator],
  );

  /**
   * Feuille du moteur v2 (`track = 'packs'`) : la session de l'interface du prototype (lot V4). Chaque ligne
   * gardée y est rejouée par la session du player (`acceptText`), qui l'enregistre et compte ses HORA : les
   * deux restent alignées. null : pas une feuille v2, ou interface indisponible (l'ancienne interface joue).
   */
  const getV2 = useCallback(
    (questionId: string | null): V2Session | null => {
      if (!questionId) return null;
      const entry = data.data?.models.get(questionId);
      if (!entry) return getFreeV2(questionId);
      const ui = data.data?.v2;
      if (!ui || entry.track !== 'packs') return null;
      const base = get(questionId);
      if (!base) return null;
      let v = v2Sessions.current.get(questionId);
      if (!v) {
        try {
          const input = freeRef.current?.modelFor?.(questionId);
          v = new ui.V2Session({
            model: entry.model,
            moreKeys: !!entry.moreKeys,
            ...(input ? { statement: { promptMd: input.promptMd, level: input.level } } : {}),
            onCommit: (line) =>
              base.acceptText(line.text, line.hinted, !!line.revealed, line.simplified),
          });
          const stored = data.data?.lines.get(questionId);
          if (stored?.length) v.replay(stored);
          if (base.getSnapshot().locked) v.lock();
        } catch (e) {
          console.warn('[scratch] interface v2 ignorée', questionId, e);
          return null;
        }
        v2Sessions.current.set(questionId, v);
      }
      return v;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getFreeV2 lit ses entrées à l'appel
    [data.data, get, lazyUi],
  );

  /**
   * Feuille LIBRE du moteur v2 (question sans feuille publiée) : modèle construit à partir de l'énoncé et des
   * propositions (`freeV2Model`), sans HORA ; ses lignes restent sur l'appareil. null : pas de feuille libre ici,
   * ou interface pas encore chargée (elle arrive, la feuille suit au rendu suivant).
   */
  function getFreeV2(questionId: string): V2Session | null {
    const f = freeRef.current;
    const input = f?.modelFor?.(questionId);
    if (!f || !input || data.isLoading) return null;
    const ui = data.data?.v2 ?? lazyUi;
    if (!ui) {
      requestUi();
      return null;
    }
    let v = v2Sessions.current.get(questionId);
    if (v) return v;
    if (broken.current.has(questionId)) return null;
    const key = record ? freeStorageKey(attemptId, questionId) : null;
    try {
      const { model, hideStart, bits } = ui.freeV2Model(input);
      const created: V2Session = new ui.V2Session({
        model,
        free: true,
        hideStart,
        statementBits: bits,
        onCommit: key
          ? () => {
              saveFreeTextLines(
                key,
                created.lines.filter((l) => !l.stopped).map((l) => ({ text: l.text })),
              );
              return 0;
            }
          : () => 0,
      });
      const stored = loadFreeTextLines(key);
      if (stored.length)
        created.replay(stored.map((l) => ({ text: l.text, kind: 'kept', hora: 0, hinted: false })));
      if (get(questionId)?.getSnapshot().locked) created.lock();
      v = created;
    } catch (e) {
      console.warn('[scratch] feuille libre v2 ignorée', questionId, e);
      broken.current.add(questionId);
      return null;
    }
    v2Sessions.current.set(questionId, v);
    return v;
  }

  const isDraft = useCallback(
    (questionId: string | null) => !!questionId && !!data.data?.models.get(questionId)?.draft,
    [data.data],
  );

  /** Questions répondues : leurs feuilles se verrouillent (relecture seule). */
  const lockAnswered = useCallback((answered: (questionId: string) => boolean) => {
    sessions.current.forEach((s, qid) => {
      if (answered(qid)) s.lock();
    });
    v2Sessions.current.forEach((s, qid) => {
      if (answered(qid) && !s.locked) s.lock();
    });
  }, []);

  /** HORA de brouillon de chaque question (bilan : ils vont à la carte de la question). */
  const horaByQuestion = useCallback((): Record<string, number> => {
    const out: Record<string, number> = {};
    sessions.current.forEach((s, qid) => {
      const hora = s.getSnapshot().hora;
      if (hora > 0) out[qid] = hora;
    });
    return out;
  }, []);

  const v2Ui: V2Ui | null = data.data?.v2 ?? lazyUi;
  return { get, getV2, v2Ui, isDraft, lockAnswered, horaByQuestion, isLoading: data.isLoading };
}

function storedFree(l: SheetLine) {
  return { atoms: l.atoms, unchecked: !!l.unchecked };
}

const NO_FOUND: string[] = [];
const noop = () => () => {};

/** Les choix du QCM que le brouillon de l'élève établit (« ✓ Dans ton brouillon »). */
export function useFoundChoices(session: ScratchSession | null): string[] {
  return useSyncExternalStore(
    session ? session.subscribe : noop,
    () => (session ? session.getSnapshot().found : NO_FOUND),
    () => NO_FOUND,
  );
}

/**
 * Feuille d'affirmations (moteur v36) : les choix que le brouillon de l'élève montre FAUX
 * (« ✗ Fausse d'après ton brouillon »).
 */
export function useCrossedChoices(session: ScratchSession | null): string[] {
  return useSyncExternalStore(
    session ? session.subscribe : noop,
    () => (session ? session.getSnapshot().crossed : NO_FOUND),
    () => NO_FOUND,
  );
}
